import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { FileText, Plus, Trash2, Edit, Download, FileIcon, User, Eye, Network } from 'lucide-react';
import { toast } from 'sonner';
import { LinkManager } from '@/components/shared/LinkManager';
import { EntityLinkedPreview } from '@/components/shared/EntityLinkedPreview';
import { EvidenceCreateDialog } from '@/components/evidence/EvidenceCreateDialog';
import { EvidenceEditDialog } from '@/components/evidence/EvidenceEditDialog';
import { useLinkStore } from '@/stores/linkStore';
import { supabase } from '@/integrations/supabase/client';
import { formatFileSize } from '@/utils/formatFileSize';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

export default function EvidencePage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  
  const { evidence, isLoading, createEvidence, deleteEvidence, updateEvidence } = useEvidence(organization?.id);
  const { bulkCreateLinks, loadLinks } = useLinkStore();
  
  const selectedEvidence = evidence?.find(e => e.id === selectedEvidenceId);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleSave = async (formData: any, links?: SelectedEntity[]) => {
    if (!organization?.id) {
      toast.error('No organization found');
      return;
    }

    try {
      // Create evidence and get the created ID
      createEvidence(
        {
          ...formData,
          org_id: organization.id,
        },
        {
          onSuccess: async (created: any) => {
            // Create links if any
            if (links && links.length > 0 && created?.id) {
              for (const link of links) {
                await bulkCreateLinks('evidence', created.id, link.type, [link.id]);
              }
            }
          },
        }
      );

      setDialogOpen(false);
    } catch (error) {
      console.error('Error creating evidence:', error);
    }
  };

  const handleDownload = async (storagePath: string, name: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('evidence')
        .download(storagePath);

      if (error) throw error;

      // Create a download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleUpdate = async (id: string, formData: any) => {
    updateEvidence({ id, data: formData });
    setEditDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this evidence?')) {
      deleteEvidence(id);
      setDetailSheetOpen(false);
      setSelectedEvidenceId(null);
    }
  };

  const openDetailSheet = (evidenceId: string) => {
    setSelectedEvidenceId(evidenceId);
    setDetailSheetOpen(true);
  };

  // Filter evidence
  const filteredEvidence = evidence?.filter(item => {
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    if (filterAssigned && item.owner_id !== profile?.user_id) return false;
    return true;
  }) || [];

  if (isLoading) {
    return <div>Loading evidence...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Evidence</h1>
          <p className="text-muted-foreground">Manage compliance evidence and documentation</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Evidence
        </Button>
      </div>

      <div className="flex gap-4 items-center">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="document">Document</SelectItem>
            <SelectItem value="policy">Policy</SelectItem>
            <SelectItem value="screenshot">Screenshot</SelectItem>
            <SelectItem value="audit">Audit Report</SelectItem>
            <SelectItem value="certificate">Certificate</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        
        <Button 
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      <div className="space-y-4">
        {!filteredEvidence || filteredEvidence.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No evidence found</h3>
              <p className="text-muted-foreground text-center mb-4">
                {typeFilter !== 'all' || filterAssigned 
                  ? 'No evidence matches your filters. Try adjusting them.'
                  : 'Start documenting compliance evidence for your organization.'}
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Evidence
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredEvidence.map((item) => (
            <Card 
              key={item.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openDetailSheet(item.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {item.storage_path ? (
                        <FileIcon className="w-5 h-5 text-foreground" />
                      ) : (
                        <FileText className="w-5 h-5 text-foreground" />
                      )}
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <Badge variant="secondary">{item.type}</Badge>
                    </div>
                    <CardDescription>
                      {item.file_size && `${formatFileSize(item.file_size)} · `}
                      Created {new Date(item.created_at).toLocaleDateString()}
                    </CardDescription>
                    {/* Show linked entities */}
                    <div className="mt-2">
                      <EntityLinkedPreview entityType="evidence" entityId={item.id} maxDisplay={3} />
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetailSheet(item.id)}
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {item.storage_path && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(item.storage_path!, item.name)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEvidenceId(item.id);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {(item as any).owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{(item as any).owner.name || (item as any).owner.email}</p>
                    </div>
                  )}
                  {item.notes && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Notes</p>
                      <p className="font-medium truncate">{item.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <EvidenceCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
      />

      {selectedEvidence && (
        <EvidenceEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={(data) => handleUpdate(selectedEvidence.id, data)}
          evidence={selectedEvidence}
        />
      )}

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen && !editDialogOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedEvidence && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle className="flex items-center gap-2">
                    {selectedEvidence.storage_path ? (
                      <FileIcon className="w-5 h-5" />
                    ) : (
                      <FileText className="w-5 h-5" />
                    )}
                    Evidence Details
                  </SheetTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDetailSheetOpen(false);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(selectedEvidence.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{selectedEvidence.name}</h3>
                  <Badge variant="secondary">{selectedEvidence.type}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedEvidence.file_size && (
                    <div>
                      <p className="text-muted-foreground">File Size</p>
                      <p className="font-medium">{formatFileSize(selectedEvidence.file_size)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">{new Date(selectedEvidence.created_at).toLocaleDateString()}</p>
                  </div>
                  {selectedEvidence.review_date && (
                    <div>
                      <p className="text-muted-foreground">Review Date</p>
                      <p className="font-medium">{new Date(selectedEvidence.review_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>

                {selectedEvidence.storage_path && (
                  <div>
                    <Button
                      onClick={() => handleDownload(selectedEvidence.storage_path!, selectedEvidence.name)}
                      className="w-full"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
                
                {selectedEvidence.notes && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground">{selectedEvidence.notes}</p>
                  </div>
                )}
                
                <Separator />
                
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Network className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Related Entities</h4>
                  </div>
                  <LinkManager
                    entityType="evidence"
                    entityId={selectedEvidence.id}
                    entityName={selectedEvidence.name}
                    allowedTypes={['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'risk', 'control', 'supplier', 'incident', 'task']}
                    showHeader={false}
                  />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
